import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BlushingModal, LoaderPulse, Text } from '@reservamos/elements';
import CSSVariablesProvider from 'components/CSSVariablesProvider';
import QuickCheckout from 'components/purchase/QuickCheckout';
import { MercadoPagoTDCProvider } from 'components/mercadoPago';
import { CieloTDCProvider } from 'components/cielo/CieloTDCContext';
import withPurchaseTimer from 'hocs/withPurchaseTimer';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { setAlert } from '../../../actions';
import { mercadoPagoPaymentAttempt } from '../../../actions/payment';
import { resetPurchase, unlockSeats } from '../../../actions/purchase';

const buildInitialFormMetadata = (t) => {
  return {
    id: 'mercado_pago_form',
    fields: {
      cardNumber: {
        id: 'mercado_pago_form__cardNumber',
        placeholder: t('payment:card_number'),
        hasError: false,
        isValid: false,
      },
      expirationDate: {
        id: 'mercado_pago_form__expirationDate',
        placeholder: t('payment:card_expiration_date'),
        hasError: false,
        isValid: false,
      },
      securityCode: {
        id: 'mercado_pago_form__securityCode',
        placeholder: t('payment:card_CVV'),
        hasError: false,
        isValid: false,
      },
      cardholderName: {
        id: 'mercado_pago_form__cardholderName',
        placeholder: t('payment:card_owner_name'),
        hasError: false,
        isValid: false,
      },
      issuer: {
        id: 'mercado_pago_form__issuer',
        placeholder: t('payment:issuer_bank'),
        hasError: false,
        isValid: false,
      },
      identificationType: {
        id: 'mercado_pago_form__identificationType',
        hasError: false,
        isValid: false,
      },
      identificationNumber: {
        id: 'mercado_pago_form__identificationNumber',
        placeholder: t('payment:identification_number'),
        hasError: false,
        isValid: false,
      },
      cardholderEmail: {
        id: 'mercado_pago_form__cardholderEmail',
        placeholder: t('passengers:email'),
        hasError: false,
        isValid: false,
      },
      installments: {
        id: 'mercado_pago_form__installments',
        placeholder: t('payment:payment_installments'),
        hasError: false,
        isValid: false,
      },
    },
  };
};

const QuickPurchasePaymentProviders = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const initialFormMetadata = useMemo(() => buildInitialFormMetadata(t), [t]);

  const handleMercadoPagoSubmit = (formData) => {
    dispatch(mercadoPagoPaymentAttempt(formData));
  };

  return (
    <MercadoPagoTDCProvider
      onSubmit={handleMercadoPagoSubmit}
      initialFormMetadata={initialFormMetadata}
    >
      <CieloTDCProvider>{children}</CieloTDCProvider>
    </MercadoPagoTDCProvider>
  );
};

QuickPurchasePaymentProviders.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Renders the quick checkout flow with the purchase timer data.
 * @param {object} props - Component props.
 * @param {object} props.history - React Router history object.
 * @param {object} props.location - React Router location object.
 * @param {Function} props.goToSearch - Callback to close the modal or go back to search.
 * @param {Function} props.onClose - Callback to close modal mode.
 * @param {object} props.orchestrationParams - Quick checkout orchestration params.
 * @param {number} props.timerRemainingTime - Remaining purchase time.
 * @returns {JSX.Element} The quick checkout flow wrapped in payment providers.
 */
const QuickPurchaseModalCheckoutBase = ({
  history,
  location,
  goToSearch,
  onClose,
  orchestrationParams,
  timerRemainingTime,
}) => {
  return (
    <QuickPurchasePaymentProviders>
      <QuickCheckout
        history={history}
        location={location}
        goToSearch={goToSearch}
        isModal
        onClose={onClose}
        orchestrationParams={orchestrationParams}
        timerRemainingTime={timerRemainingTime}
      />
    </QuickPurchasePaymentProviders>
  );
};

QuickPurchaseModalCheckoutBase.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  goToSearch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  orchestrationParams: PropTypes.shape({
    passengersParam: PropTypes.string,
    selectedSeatNumbers: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  timerRemainingTime: PropTypes.number,
};

QuickPurchaseModalCheckoutBase.defaultProps = {
  timerRemainingTime: 0,
};

const QuickPurchaseModalCheckout = withPurchaseTimer(QuickPurchaseModalCheckoutBase);

const QuickPurchaseModal = ({
  isOpen,
  payload,
  onClose,
  createQuickPurchase,
  purchaseToken,
  purchaseLoaded,
  onTokenReady,
  history,
  location,
}) => {
  const { theme, brand } = useWhitelabelEnvs();
  const { FUNNEL_STYLE } = useWhitelabelFeatures();
  const dispatch = useDispatch();
  const isExpired = useSelector((state) => state.purchase.get('isExpired'));
  const { t } = useTranslation();
  const [creationError, setCreationError] = useState(null);
  const requestedPayloadKeyRef = useRef(null);
  const generationRef = useRef(0);
  const resolvedTokenRef = useRef(null);

  const passengersParam = payload?.passengersParam || 'A1';
  const selectedSeatNumbers = payload?.seatNumbers || null;
  const targetToken = payload?.quickPurchaseToken || null;

  const hasExpectedPurchase = Boolean(
    purchaseToken &&
      purchaseLoaded &&
      (targetToken
        ? targetToken === purchaseToken
        : resolvedTokenRef.current && resolvedTokenRef.current === purchaseToken),
  );

  useEffect(() => {
    if (!isOpen) {
      setCreationError(null);
      requestedPayloadKeyRef.current = null;
      resolvedTokenRef.current = null;
      generationRef.current += 1;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !payload || targetToken) return;

    const payloadKey = JSON.stringify({
      departureId: payload.departureId,
      returnId: payload.returnId,
      passengersParam,
      seatNumbers: payload.seatNumbers || [],
      seenPrice: payload.seenPrice,
    });

    if (requestedPayloadKeyRef.current === payloadKey) return;

    requestedPayloadKeyRef.current = payloadKey;
    resolvedTokenRef.current = null;
    setCreationError(null);

    generationRef.current += 1;
    const myGeneration = generationRef.current;

    createQuickPurchase({
      departureId: payload.departureId,
      returnId: payload.returnId,
      passengersParam,
      seenPrice: payload.seenPrice,
    }).then((token) => {
      if (myGeneration !== generationRef.current) {
        if (token) {
          unlockSeats({
            purchaseToken: token,
            forceUnlock: true,
            shouldUpdateState: false,
            dispatch,
          });
        }
        return;
      }

      if (!token) {
        setCreationError(t('purchase:quick_purchase_error'));
        requestedPayloadKeyRef.current = null;
        return;
      }

      resolvedTokenRef.current = token;
      if (onTokenReady) {
        onTokenReady(token);
      }
    });
  }, [
    createQuickPurchase,
    dispatch,
    isOpen,
    onTokenReady,
    passengersParam,
    payload,
    targetToken,
    t,
  ]);

  useEffect(() => {
    if (!isExpired || !isOpen) return;
    onClose();
    dispatch(setAlert('finished_time_for_purchase', 'warning'));
    dispatch(resetPurchase());
  }, [isExpired, isOpen, onClose, dispatch]);

  const handleClose = () => {
    if (purchaseToken) {
      unlockSeats({ purchaseToken, forceUnlock: true, shouldUpdateState: false, dispatch });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <BlushingModal
      defaultOpened
      onCloseModal={handleClose}
      responsiveSize="L"
      expandedModal
      fullHeightDesktop
    >
      <CSSVariablesProvider
        theme={theme}
        funnelStyle={FUNNEL_STYLE}
        funnelStyleBrandVariation={brand.toUpperCase()}
      >
        <div className="w-full mt-4">
          {!hasExpectedPurchase && !creationError && (
            <div className="flex items-center justify-center lg:min-h-[70vh]">
              <LoaderPulse color="accent" hasWrapper={false} />
            </div>
          )}

          {creationError && (
            <div className="py-10 px-6 text-center">
              <Text size="L" color="error">
                {creationError}
              </Text>
            </div>
          )}

          {hasExpectedPurchase && !creationError && (
            <QuickPurchaseModalCheckout
              history={history}
              location={location}
              goToSearch={handleClose}
              onClose={handleClose}
              orchestrationParams={{
                passengersParam,
                selectedSeatNumbers,
              }}
            />
          )}
        </div>
      </CSSVariablesProvider>
    </BlushingModal>
  );
};

QuickPurchaseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  payload: PropTypes.shape({
    departureId: PropTypes.string,
    returnId: PropTypes.string,
    passengersParam: PropTypes.string,
    seenPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    seatNumbers: PropTypes.arrayOf(PropTypes.string),
    quickPurchaseToken: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  createQuickPurchase: PropTypes.func.isRequired,
  purchaseToken: PropTypes.string,
  purchaseLoaded: PropTypes.bool,
  onTokenReady: PropTypes.func,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

QuickPurchaseModal.defaultProps = {
  payload: null,
  purchaseToken: '',
  purchaseLoaded: false,
  onTokenReady: null,
};

export default QuickPurchaseModal;
